import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import ScoreCell from '../components/ScoreCell';
import Avatar from '../components/Avatar';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useScoring } from '../lib/scoring';
import { SkeletonTable, SkeletonHeader, SkeletonBreadcrumb } from '../components/Skeleton';

export default function Clinicians() {
  const { profile } = useAuth();
  const { threshold, weights } = useScoring();
  const [search, setSearch] = useState('');
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    api.clinicians.list()
      .then(setClinicians)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [profile?.id]);

  const filteredClinicians = clinicians.filter(c =>
    c.full_name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div>
      <SkeletonBreadcrumb />
      <SkeletonHeader />
      <SkeletonTable rows={6} />
    </div>
  );

  return (
    <div>
      <Breadcrumb items={[
        { label: 'Home', to: '/' },
        { label: 'Clinicians' },
      ]} />

      <h1 className="text-2xl md:text-[32px] font-semibold text-text mb-6">Clinicians</h1>

      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
          <h3 className="text-xl font-bold text-text">All Clinicians</h3>
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-200 rounded-lg px-4 py-2 text-sm w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
          />
        </div>

        <table className="w-full hidden sm:table">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 text-sm font-semibold text-gray-700">Name</th>
              <th className="text-center py-3 text-sm font-semibold text-gray-700 w-48">Average CScore</th>
            </tr>
          </thead>
          <tbody>
            {filteredClinicians.map((clinician) => (
              <tr key={clinician.id} className="border-b border-gray-50 last:border-0">
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <Avatar src={clinician.image_url} name={clinician.full_name} size="md" />
                    <div>
                      <Link to={`/clinicians/${clinician.id}`} className="text-primary font-medium hover:underline text-sm">{clinician.full_name}</Link>
                      <p className="text-xs text-gray-500 mt-0.5">Clinician ID: {clinician.kipu_clinician_id}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 text-center">
                  <ScoreCell
                    score={clinician.avg_cscore}
                    breakdown={clinician.breakdown}
                    weights={weights}
                    threshold={threshold}
                    title={`${clinician.full_name} average`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="sm:hidden">
          {filteredClinicians.map((clinician, idx) => (
            <div key={clinician.id} className={`flex items-center justify-between py-3 ${idx !== filteredClinicians.length - 1 ? 'border-b border-gray-100' : ''}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Avatar src={clinician.image_url} name={clinician.full_name} size="md" />
                <div className="min-w-0">
                  <Link to={`/clinicians/${clinician.id}`} className="text-primary font-medium hover:underline text-sm truncate block">{clinician.full_name}</Link>
                  <p className="text-xs text-gray-500">ID: {clinician.kipu_clinician_id}</p>
                </div>
              </div>
              <ScoreCell
                score={clinician.avg_cscore}
                breakdown={clinician.breakdown}
                weights={weights}
                threshold={threshold}
                title={`${clinician.full_name} average`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
